import { MathUtil, Radian } from "./MathUtil";
import { IVec3 } from "./Vec3";

export interface IVec2 {
  x: number;
  y: number;
}

export class Vec2 {
  static from(x: number, y: number): IVec2 {
    return { x, y };
  }

  static fromXZ(v: IVec3): IVec2 {
    // if (!v) {
    //   console.log("HERE!");
    // }
    return { x: v.x, y: v.z };
  }

  static clone(a: IVec2): IVec2 {
    return { x: a.x, y: a.y };
  }

  static add(a: IVec2, b: IVec2): IVec2 {
    return {
      x: a.x + b.x,
      y: a.y + b.y,
    };
  }

  static addScaled(a: IVec2, b: IVec2, s: number): IVec2 {
    return {
      x: a.x + b.x * s,
      y: a.y + b.y * s,
    };
  }

  static addElements(a: IVec2, x: number, y: number): IVec2 {
    return {
      x: a.x + x,
      y: a.y + y,
    };
  }

  static sub(a: IVec2, b: IVec2): IVec2 {
    return {
      x: a.x - b.x,
      y: a.y - b.y,
    };
  }

  static to(a: IVec2, b: IVec2): IVec2 {
    return Vec2.sub(b, a);
  }

  static equals(a: IVec2, b: IVec2): boolean {
    return a.x === b.x && a.y === b.y;
  }

  static mul(a: IVec2, s: number): IVec2 {
    return {
      x: a.x * s,
      y: a.y * s,
    };
  }

  static scale(a: IVec2, s: IVec2): IVec2 {
    return {
      x: a.x * s.x,
      y: a.y * s.y,
    };
  }

  static mid(a: IVec2, b: IVec2): IVec2 {
    return {
      x: (a.x + b.x) * 0.5,
      y: (a.y + b.y) * 0.5,
    };
  }

  static len(a: IVec2, b?: IVec2): number {
    return Math.sqrt(Vec2.lenSqr(a, b));
  }

  static lenSqr(a: IVec2, b?: IVec2): number {
    if (b !== undefined) {
      const ab = Vec2.to(a, b);
      return ab.x * ab.x + ab.y * ab.y;
    }
    return a.x * a.x + a.y * a.y;
  }

  static withLen(a: IVec2, len: number): IVec2 {
    return Vec2.mul(Vec2.normalized(a), len);
  }

  static normalized(a: IVec2): IVec2 {
    const l = Vec2.len(a);
    return {
      x: a.x / l,
      y: a.y / l,
    };
  }

  static negate(a: IVec2): IVec2 {
    return {
      x: -a.x,
      y: -a.y,
    };
  }

  static round(a: IVec2): IVec2 {
    return {
      x: Math.round(a.x),
      y: Math.round(a.y)
    }
  }

  static normalVector(a: IVec2, negative?: boolean): IVec2;
  static normalVector(a: IVec2, b: IVec2, negative?: boolean): IVec2;
  static normalVector(a: IVec2, b?: IVec2 | boolean, c?: boolean): IVec2 {
    let v: IVec2 = a;
    let positive: boolean;
    if (typeof b === "object") {
      positive = c !== undefined ? c : true;
      v = Vec2.to(a, b);
    } else {
      positive = !b;
    }
    return positive ? { x: -v.y, y: v.x } : { x: v.y, y: -v.x };
  }

  static dot(a: IVec2, b: IVec2): number {
    return a.x * b.x + a.y * b.y;
  }

  static det(a: IVec2, b: IVec2): number {
    return Vec2.cross(a, b);
  }

  static cross(a: IVec2, b: IVec2): number {
    return a.x * b.y - a.y * b.x;
  }

  static dir(a: IVec2, b: IVec2): IVec2 {
    return Vec2.normalized(Vec2.sub(b, a));
  }

  static dirLen(a: IVec2, b: IVec2): { dir: IVec2, len: number } {
    const ab = Vec2.to(a, b);
    const len = Vec2.len(a, b);
    return {
      dir: Vec2.mul(ab, 1 / len),
      len
    }
  }

  static rotate(a: IVec2, angle: Radian, origin?: IVec2): IVec2 {
    const c = Math.cos(angle);
    const s = Math.sin(angle);

    let dx = a.x;
    let dy = a.y;
    if (origin) {
      dx -= origin.x;
      dy -= origin.y;
    }

    let x = c * dx - s * dy;
    let y = s * dx + c * dy;
    if (origin) {
      x += origin.x;
      y += origin.y;
    }

    return { x, y };
  }


  static lerp(a: IVec2, b: IVec2, posFactor: number): IVec2 {
    return {
      x: a.x + (b.x - a.x) * posFactor,
      y: a.y + (b.y - a.y) * posFactor,
    };
  }

  static rotateInPlace(a: IVec2, angle: number, origin?: IVec2): void {
    if (angle === null) return;
    let dx = a.x;
    let dy = a.y;
    if (origin) {
      dx -= origin.x;
      dy -= origin.y;
    }
    let nx = Math.cos(angle) * dx - Math.sin(angle) * dy;
    let ny = Math.sin(angle) * dx + Math.cos(angle) * dy;
    if (origin) {
      nx += origin.x;
      ny += origin.y;
    }
    a.x = nx;
    a.y = ny;
  }

  static angleBetween(a: IVec2, b: IVec2, shortest = false, c?: IVec2): Radian {
    if (c === undefined) {
      const dot = Vec2.dot(Vec2.normalized(a), Vec2.normalized(b));
      const ret = Math.acos(MathUtil.clamp(dot, -1, +1));
      return shortest ? (ret > Math.PI ? Math.PI - ret : ret) : ret;
    } else {
      const ab = Vec2.to(a, b);
      const ac = Vec2.to(a, c);
      return Vec2.angleBetween(ab, ac, shortest);
    }
  }

  static calcAngle(pre: IVec2, a: IVec2, next: IVec2): Radian {
    const ap = Vec2.dir(a, pre);
    const an = Vec2.dir(a, next);
    const angle = Math.acos(Vec2.dot(ap, an));
    return angle;
  }

  static angleAbsolute(a: IVec2, b: IVec2): Radian {
    // const dot = IVec2.dot(a, b)
    // const det = IVec2.det(a, b)
    // const angle = Math.atan2(det, dot)
    const angle = Math.atan2(b.y, b.x) - Math.atan2(a.y, a.x);
    return angle < 0 ? angle + Math.PI * 2 : angle;
  }

  static project(a: IVec2, onto: IVec2): IVec2 {
    const normalizedOnto = Vec2.normalized(onto);
    const scale = Vec2.dot(a, normalizedOnto);
    return Vec2.mul(normalizedOnto, scale);
  }

  static center(points: IVec2[]): IVec2 {
    const sum = Vec2.from(0, 0);
    for (const p of points) {
      sum.x += p.x;
      sum.y += p.y;
    }
    sum.x = sum.x / points.length;
    sum.y = sum.y / points.length;
    return sum;
  }


  static ZERO: IVec2 = { x: 0.0, y: 0.0 }
  static ONE: IVec2 = { x: 1.0, y: 1.0 }
  static NAN: IVec2 = { x: Number.NaN, y: Number.NaN }
}
